import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { SyncCentresResult } from "../../../api/appointment";

type Props = {
  result: SyncCentresResult;
  onDismiss: () => void;
};

const MAX_VISIBLE_ERRORS = 20;

/**
 * Post-sync report.
 *
 * Rendered on the page rather than inside the modal, and NOT auto-dismissed:
 * the failure list is the only place a skipped centre is ever named. A toast
 * that disappears after four seconds would leave "12 centres non traités"
 * with no way to find out which twelve.
 */
export const SyncCentresReport: FC<Props> = ({ result, onDismiss }) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const [showMissing, setShowMissing] = useState(false);

  const partial = result.status === "partial_success";
  const failed = Math.max(
    0,
    result.total_processed - result.successfully_processed
  );

  const errors = result.errors ?? [];
  const missing = result.centres_missing_addresses ?? [];

  return (
    <Alert
      severity={partial ? "warning" : "success"}
      sx={{ mb: 2 }}
      onClose={onDismiss}
    >
      <AlertTitle>
        {partial
          ? t("centres.reportPartialTitle", "Synchronisation terminée avec des erreurs")
          : t("centres.reportOkTitle", "Synchronisation terminée")}
      </AlertTitle>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
        <Chip
          size="small"
          label={`${t("centres.processed", "Traités")}: ${result.total_processed}`}
        />
        <Chip
          size="small"
          color="success"
          label={`${t("centres.succeeded", "Réussis")}: ${result.successfully_processed}`}
        />
        {failed > 0 && (
          <Chip
            size="small"
            color="error"
            label={`${t("centres.failed", "En échec")}: ${failed}`}
          />
        )}
        {result.centres_without_address > 0 && (
          <Chip
            size="small"
            color="warning"
            label={`${t("centres.noAddress", "Sans adresse")}: ${result.centres_without_address}`}
          />
        )}
      </Box>

      {/*
        A centre with no address_id is invisible on the map and unreachable
        from the booking flow, so it counts as broken even though the sync
        reported it as processed. Called out separately for that reason.
      */}
      {result.centres_without_address > 0 && (
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {t(
            "centres.noAddressHelp",
            "Un centre sans adresse n'apparaît ni sur la carte ni dans la prise de rendez-vous."
          )}
        </Typography>
      )}

      {errors.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setShowErrors((v) => !v)}
            sx={{ textTransform: "none", pl: 0 }}
          >
            {showErrors
              ? t("centres.hideErrors", "Masquer les erreurs")
              : t("centres.showErrors", {
                  count: errors.length,
                  defaultValue: `Voir les ${errors.length} erreur(s)`,
                })}
          </Button>

          <Collapse in={showErrors}>
            <Box
              sx={{
                mt: 0.5,
                maxHeight: 220,
                overflowY: "auto",
                fontFamily: "monospace",
                fontSize: "0.75rem",
              }}
            >
              {errors.slice(0, MAX_VISIBLE_ERRORS).map((e, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  display="block"
                  sx={{ fontFamily: "inherit" }}
                >
                  {e}
                </Typography>
              ))}
              {errors.length > MAX_VISIBLE_ERRORS && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                  {t("centres.moreErrors", {
                    count: errors.length - MAX_VISIBLE_ERRORS,
                    defaultValue: `… et ${errors.length - MAX_VISIBLE_ERRORS} autre(s). Détail complet dans les Journaux.`,
                  })}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {missing.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setShowMissing((v) => !v)}
            sx={{ textTransform: "none", pl: 0 }}
          >
            {showMissing
              ? t("centres.hideMissing", "Masquer les centres sans adresse")
              : t("centres.showMissing", {
                  count: missing.length,
                  defaultValue: `Voir les ${missing.length} centre(s) sans adresse`,
                })}
          </Button>

          <Collapse in={showMissing}>
            <TableContainer component={Paper} sx={{ mt: 0.5, maxHeight: 220 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t("centres.name", "Nom")}</TableCell>
                    <TableCell>{t("centres.code", "Code centre")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missing.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.id}</TableCell>
                      <TableCell>{c.name ?? "—"}</TableCell>
                      <TableCell>{c.centre_code ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>
      )}
    </Alert>
  );
};
